import { IS_PROD } from "@/shared/lib/feature-flags";

/**
 * Is this the GTM portal host (gtm.theubik.com) rather than the workbench?
 *
 * One build and one Cloud Run service serve both hosts, so this is decided at
 * runtime from the hostname. On the GTM host App.tsx renders a GTM-only shell
 * (GtmApp) instead of the workbench, and `/` lands on `/gtm`.
 *
 * `?host=gtm` is a sticky override for local dev and staging, where there is no
 * gtm.* hostname: it is stored in sessionStorage so it survives the OAuth round
 * trip (which comes back to `/?session=…` with the param gone). `?host=app`
 * clears it. Ignored in a production build, where the real host exists.
 */
export const GTM_HOST_OVERRIDE_KEY = "ubik_gtm_host";

type LocationLike = Pick<Location, "hostname" | "search">;
type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export function detectGtmHost(
  location: LocationLike,
  storage: StorageLike | null,
  allowOverride: boolean,
): boolean {
  if (location.hostname.toLowerCase().startsWith("gtm.")) return true;
  if (!allowOverride || !storage) return false;
  try {
    const requested = new URLSearchParams(location.search).get("host");
    if (requested === "gtm") storage.setItem(GTM_HOST_OVERRIDE_KEY, "gtm");
    else if (requested !== null) storage.removeItem(GTM_HOST_OVERRIDE_KEY);
    return storage.getItem(GTM_HOST_OVERRIDE_KEY) === "gtm";
  } catch {
    // Blocked storage (private mode, cookie policy): no override, workbench as usual.
    return false;
  }
}

function sessionStore(): StorageLike | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

let cached: boolean | null = null;

/** Memoised for the page's lifetime: the host cannot change without a reload. */
export function isGtmHost(): boolean {
  if (cached === null) cached = detectGtmHost(window.location, sessionStore(), !IS_PROD);
  return cached;
}
